import { css, html } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import { FlensSliderController } from './slider-controller';

export class FlensSliderNavigation extends FlensSliderController {
	static properties = {
		loop: { type: Boolean },
		_canGoPrevious: { type: Boolean, state: true },
		_canGoNext: { type: Boolean, state: true },
	};

	static styles = css`
    :host {
      align-items: center;
      box-sizing: border-box;
      display: flex;
      justify-content: space-between;
      inline-size: 100%;
      inset-block-start: 50%;
      pointer-events: none;
      position: absolute;
      transform: translateY(-50%);
      z-index: 3;
    }

    .previous,
    .next {
      align-items: center;
      border-radius: 50%;
      cursor: pointer;
      color: inherit;
      display: flex;
      justify-content: center;
      outline-offset: 0.25rem;
      padding: 0.25rem;
      pointer-events: all;
      transition: 0.3s all ease-out;

      &:disabled {
        cursor: not-allowed;
      }
    }

    ::slotted(*),
    svg {
      fill: currentColor;
    }
  `;

	constructor() {
		super();
		this._previousRef = createRef();
		this._nextRef = createRef();
		this.loop = false;
		this._canGoPrevious = false;
		this._canGoNext = false;
		this._onSliderChange = this._onSliderChange.bind(this);
	}

	/** @returns {HTMLElement} */
	get nextButton() {
		return this._nextRef.value;
	}

	/** @returns {HTMLElement} */
	get previousButton() {
		return this._previousRef.value;
	}

	/** @returns {boolean} */
	get canGoPrevious() {
		return this.loop || this._canGoPrevious;
	}

	/** @returns {boolean} */
	get canGoNext() {
		return this.loop || this._canGoNext;
	}

	_onClickPrevious() {
		const { controlledSlider } = this;
		if (!controlledSlider) {
			return;
		}
		if (controlledSlider.activeIndex === 0) {
			controlledSlider.moveToIndex(controlledSlider.count - 1, true);
		} else {
			controlledSlider.move(-1);
		}
		this.dispatchEvent(new Event('previous'));
	}

	_onClickNext() {
		const { controlledSlider } = this;
		if (!controlledSlider) {
			return;
		}
		if (controlledSlider.activeIndex === controlledSlider.count - 1) {
			controlledSlider.moveToIndex(0, true);
		} else {
			controlledSlider.move(1);
		}
		this.dispatchEvent(new Event('next'));
	}

	_updateControlledSlider() {
		this._controlledSlider?.removeEventListener('change', this._onSliderChange);

		super._updateControlledSlider();

		const slider = this._controlledSlider;
		if (slider) {
			slider.updateComplete?.then(this._onSliderChange);
			slider.addEventListener('change', this._onSliderChange);
		} else {
			this._canGoPrevious = false;
			this._canGoNext = false;
		}
	}

	_onSliderChange() {
		const { controlledSlider } = this;
		this._canGoPrevious = controlledSlider.canGoPrevious ?? false;
		this._canGoNext = controlledSlider.canGoNext ?? false;
	}

	render() {
		return html`
      <button
        ${ref(this._previousRef)}
        class="previous"
        part="button previous"
        ?disabled="${!this.canGoPrevious}"
        aria-label="Vorhergehender Slide"
        aria-controls="${this.controls ?? null}"
        @click="${this._onClickPrevious}"
      >
        <slot name="previous-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">
            <path
              d="M9.08 4.908 2 11.988l7.08 7.08 1.41-1.41-4.662-4.67h17.168v-2H5.828l4.662-4.67z"
            />
          </svg>
        </slot>
      </button>

      <slot></slot>

      <button
        ${ref(this._nextRef)}
        class="next"
        part="button next"
        ?disabled="${!this.canGoNext}"
        aria-label="Nächster Slide"
        aria-controls="${this.controls ?? null}"
        @click="${this._onClickNext}"
      >
        <slot name="next-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">
            <path
              d="m15.916 4.908 7.08 7.08-7.08 7.08-1.41-1.41 4.662-4.67H2v-2h17.168l-4.663-4.67Z"
            />
          </svg>
        </slot>
      </button>
    `;
	}
}

customElements.define('flens-slider-navigation', FlensSliderNavigation);
